/**
 * Money formatting. The backend stores amounts as numeric strings
 * (e.g. "12.5", "0", "120.00") and occasionally as numbers in computed
 * fields. Every column that renders a dollar value should pass through
 * `formatMoney` so adjacent columns line up under `tabular-nums`.
 *
 * The currency prefix lives at the call site so this stays composable
 * with right-aligned $-prefix columns and with inline phrases like
 * "Pay $123.45".
 */
export function formatMoney(
  value: string | number | null | undefined,
  options: { fallback?: string; minimumFractionDigits?: number } = {},
): string {
  const { fallback = '0.00', minimumFractionDigits = 2 } = options;
  if (value === null || value === undefined || value === '') return fallback;
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return fallback;
  // Grouped so large totals read at a glance ("12,345.00"). Display only —
  // never parse this back into a number.
  return n.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits: minimumFractionDigits,
  });
}

/**
 * Sum a list of numeric-string columns (backend gives us "12.50" etc.).
 * Empty / null entries count as 0. Returns a plain number so the caller
 * can decide on the final formatting.
 */
export function sumMoney(values: ReadonlyArray<string | number | null | undefined>): number {
  let total = 0;
  for (const v of values) {
    if (v === null || v === undefined || v === '') continue;
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (Number.isFinite(n)) total += n;
  }
  return total;
}
